/**
 * Company › Owned arches › releasing an arche to one person (ADR-026) — an
 * owner's act. Nothing moves until they accept; then they own it, are paid
 * its earnings and pay its costs. Who can use it stays as it is (a release
 * keeps the audience, including any grant to this Company), which the dialog
 * says, so the new owner's first look at its Access panel is no surprise.
 */

import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type { CompanyOwnedArcheDto } from '@/lib/api';
import { useReleaseCompanyArche } from '../../hooks/useArcheGovernance';
import { describeError } from './shared';

export function OwnedArcheReleaseDialog({
  companyId,
  arche,
  onClose,
}: {
  companyId: string;
  arche: CompanyOwnedArcheDto;
  onClose: () => void;
}) {
  const release = useReleaseCompanyArche(companyId);
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');

  async function send(): Promise<void> {
    try {
      await release.mutateAsync({
        archeId: arche.archeId,
        username: username.trim().replace(/^@/, ''),
        reason: reason.trim() || undefined,
      });
      toast({ title: 'Release offered', description: 'They need to accept it.' });
      onClose();
    } catch (err) {
      toast({
        title: 'Could not release',
        description: describeError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Release “{arche.name}”</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>
                Offer it to one person. When they accept, they own it and are paid its earnings, and
                this Company no longer manages it. Its other collaborators keep their roles.
              </p>
              <p>
                Who can use it stays as it is — including any access given to this Company or its
                workspaces. The new owner can change that on its Access panel.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="release-username">Username</Label>
            <Input
              id="release-username"
              value={username}
              maxLength={100}
              placeholder="@username"
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="release-reason">Note (optional)</Label>
            <Input
              id="release-reason"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!username.trim() || release.isPending}
            onClick={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            Offer release
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
